"use client"

import { useState, useMemo } from "react"
import dynamic from "next/dynamic"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { useTranslatedServices } from "@/lib/use-translated-services"
import { applyServicesConfig } from "@/lib/services-overlay"
import { Button, ArrowRightIcon } from "@/components/ui/button"
import { homeKeys as k } from "@/i18n/home-keys"
import { track } from "@/lib/analytics"
import { BookingCta } from "@/components/ui/booking-cta"
import { HeroBackgroundVideo } from "@/components/ui/hero-background-video"
import { bookingUrlForCountry, membershipBookingAppUrl } from "@/lib/booking"
import { useWaitlist } from "@/components/ui/waitlist-context"
import { isComingSoon, isWaitlistOnly } from "@/i18n/config"
import { externalQuizUrl } from "@/lib/quiz/quiz-url"
import { JsonLd } from "@/components/ui/json-ld"
import { createOrganizationSchema, createWebSiteSchema } from "@/lib/schema"

import Link from "next/link"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { useCountry } from "@/components/layout/country-provider"
import { useLivePrices } from "@/lib/use-live-prices"
import { CountryGate } from "@/components/layout/country-gate"
import { useExperimentVariant } from "@/components/analytics/experiment"
import { EXPERIMENTS } from "@/lib/experiments"
import { isAllowedInCountry } from "@/lib/country-gates"
import type { ReviewerExpert } from "@/lib/reviewer-experts"
import type { Country } from "@/i18n/config"
import type { CmsServiceConfig, Testimonial } from "@/lib/cms"
import { useNewTestsPagesEnabled } from "@/lib/use-new-tests-pages"
import { NewMembershipPill } from "@/components/ui/new-membership-pill"
import { blobMediaUrl } from "@/lib/blob-url"

/* ─── Below-fold sections — lazy-loaded JS chunks (SSR HTML still rendered) ─── */
const LogoMarquee = dynamic(() =>
  import("@/components/ui/logo-marquee").then((m) => ({
    default: m.LogoMarquee,
  }))
)
const ValuePropCards = dynamic(() =>
  import("@/components/ui/value-prop-cards").then((m) => ({
    default: m.ValuePropCards,
  }))
)
const ServicesCarousel = dynamic(() =>
  import("@/components/ui/services-carousel").then((m) => ({
    default: m.ServicesCarousel,
  }))
)
const AthleteClosingCta = dynamic(
  () =>
    import("../../go/lauraphilipps/lauraphilipps-sections").then((m) => ({
      default: m.AthleteClosingCta,
    })),
  // Gives the lazy its own Suspense boundary. Without an option next wraps it
  // in a bare Fragment (`dynamic-suspense-guard.test.ts`), which is the
  // dead-click gap for anything that can mount post-hydration.
  { loading: () => null }
)
const StatCard = dynamic(() =>
  import("@/components/ui/stat-card").then((m) => ({ default: m.StatCard }))
)
const AgeCard = dynamic(() =>
  import("@/components/ui/age-card").then((m) => ({ default: m.AgeCard }))
)
const CtaBanner = dynamic(() =>
  import("@/components/ui/cta-banner").then((m) => ({ default: m.CtaBanner }))
)
const PackageSelector = dynamic(() =>
  import("@/components/ui/package-selector").then((m) => ({
    default: m.PackageSelector,
  }))
)
const ScatteredData = dynamic(() =>
  import("@/components/ui/scattered-data").then((m) => ({
    default: m.ScatteredData,
  }))
)
const HowItWorksScroll = dynamic(() =>
  import("@/components/ui/how-it-works-scroll").then((m) => ({
    default: m.HowItWorksScroll,
  }))
)
const Step1Panel = dynamic(() =>
  import("@/components/ui/how-it-works-scroll").then((m) => ({
    default: m.Step1Panel,
  }))
)
const Step2Panel = dynamic(() =>
  import("@/components/ui/how-it-works-scroll").then((m) => ({
    default: m.Step2Panel,
  }))
)
const Step3Panel = dynamic(() =>
  import("@/components/ui/how-it-works-scroll").then((m) => ({
    default: m.Step3Panel,
  }))
)
const Step4Panel = dynamic(() =>
  import("@/components/ui/how-it-works-scroll").then((m) => ({
    default: m.Step4Panel,
  }))
)
const InsuranceSection = dynamic(() =>
  import("@/components/ui/insurance-section").then((m) => ({
    default: m.InsuranceSection,
  }))
)
const FaqSection = dynamic(() =>
  import("@/components/ui/faq-section").then((m) => ({ default: m.FaqSection }))
)
const DoctorsSection = dynamic(() =>
  import("../../../_components/doctors-section").then((m) => ({
    default: m.DoctorsSection,
  }))
)
const HomepageMembershipSection = dynamic(() =>
  import("../../../_components/homepage-membership-section").then((m) => ({
    default: m.HomepageMembershipSection,
  }))
)
const TestimonialsSection = dynamic(() =>
  import("../../../_components/testimonials-section").then((m) => ({
    default: m.TestimonialsSection,
  }))
)

/* ─── Value propositions (4 colored cards) ─── */
const VALUE_PROP_KEYS = [k.valueProp1, k.valueProp2, k.valueProp3, k.valueProp4]
// Membership-market value props, reframed around the membership action loop.
const MF_VALUE_PROP_KEYS = [
  k.mfValueProp1,
  k.mfValueProp2,
  k.mfValueProp3,
  k.mfValueProp4,
]

// Above-fold USP chips (Function-style): a bold stat + subtext, each linking to
// the proof page. Rendered in the hero, membership markets only.
const MF_USP_CHIPS = [
  {
    statKey: k.mfChipBiomarkersStat,
    subKey: k.mfChipBiomarkersSub,
    path: "/biomarkers",
  },
  {
    statKey: k.mfChipInsuranceStat,
    subKey: k.mfChipInsuranceSub,
    path: "/insurance",
  },
  {
    statKey: k.mfChipMriStat,
    subKey: k.mfChipMriSub,
    path: "/tests/full-body-mri",
  },
] as const

/* ─── Stats (section 5) ─── */

const STATS_META = [
  {
    valueKey: k.statValue1,
    descKey: k.statDesc1,
    highlightKey: k.statHighlight1,
    highlightColor: "#8E9B15",
    sourceKey: k.statSourceLancet,
    sourceUrl:
      "https://www.thelancet.com/journals/lancet/article/PIIS0140-6736(04)17018-9/abstract",
  },
  {
    valueKey: k.statValue2,
    descKey: k.statDesc2,
    highlightKey: k.statHighlight2,
    highlightColor: "#2C7FDD",
    sourceKey: k.statSourceClinicalData,
  },
  {
    valueKey: k.statValue3,
    descKey: k.statDesc3,
    highlightKey: k.statHighlight3,
    highlightColor: "#7F4685",
    sourceKey: k.statSourceClinicalData,
  },
]

/* ─── Logo marquee ─── */

type LogoTagKey = "partnerWith" | "experienceAt" | "asSeenOn"

const LOGO_ITEMS: {
  src: string
  alt: string
  h: number
  tagKey: LogoTagKey
  href?: string
  /** When set, the logo only renders in these countries. Omit = all countries. */
  countries?: Country[]
}[] = [
  {
    src: blobMediaUrl("/images/logos/unilabs.svg"),
    alt: "Unilabs",
    h: 28,
    tagKey: "partnerWith",
  },
  {
    src: blobMediaUrl("/images/logos/oxford.svg"),
    alt: "Oxford",
    h: 28,
    tagKey: "experienceAt",
  },
  {
    src: blobMediaUrl("/images/logos/google.svg"),
    alt: "Google",
    h: 28,
    tagKey: "experienceAt",
  },
  {
    src: blobMediaUrl("/images/logos/newzapiens.svg"),
    alt: "newzapiens",
    h: 28,
    tagKey: "partnerWith",
  },
  {
    src: blobMediaUrl("/images/logos/siemens.svg"),
    alt: "Siemens Healthineers",
    h: 28,
    tagKey: "partnerWith",
  },
  {
    src: blobMediaUrl("/images/logos/quote.svg"),
    alt: "QUOTE",
    h: 28,
    tagKey: "asSeenOn",
    href: "https://www.quotenet.nl/zakelijk/a69976033/cody-gakpo-en-anderen-storten-6-miljoen-in-pre-scan-voor-millennials/",
  },
  {
    src: blobMediaUrl("/images/logos/tum.svg"),
    alt: "TUM",
    h: 28,
    tagKey: "experienceAt",
  },
  {
    src: blobMediaUrl("/images/logos/techeu.svg"),
    alt: "tech.eu",
    h: 28,
    tagKey: "asSeenOn",
    href: "https://tech.eu/2026/01/13/ahead-health-raises-6m-to-build-ai-powered-health-system-in-europe/",
  },
  {
    src: blobMediaUrl("/images/logos/kaia.svg"),
    alt: "Kaia Health",
    h: 28,
    tagKey: "experienceAt",
  },
  {
    src: blobMediaUrl("/images/logos/coolblue.svg"),
    alt: "Coolblue",
    h: 24,
    tagKey: "experienceAt",
    countries: ["nl"],
  },
  {
    src: blobMediaUrl("/images/logos/picnic.svg"),
    alt: "Picnic",
    h: 24,
    tagKey: "experienceAt",
    countries: ["nl"],
  },
]

/* ─── Age groups (section 9) ─── */

const AGE_GROUPS_META = [
  {
    rangeKey: k.age30sRange,
    titleKey: k.age30sTitle,
    descKey: k.age30sDescription,
    linkKey: k.age30sLink,
    image: blobMediaUrl("/images/homepage/age-30s.jpg"),
    linkPath: "/biomarkers",
  },
  {
    rangeKey: k.age40sRange,
    titleKey: k.age40sTitle,
    descKey: k.age40sDescription,
    linkKey: k.age40sLink,
    image: blobMediaUrl("/images/homepage/age-40s.jpg"),
    linkPath: "/what-we-detect",
  },
  {
    rangeKey: k.age60sRange,
    titleKey: k.age60sTitle,
    descKey: k.age60sDescription,
    linkKey: k.age60sLink,
    image: blobMediaUrl("/images/homepage/age-60s.jpg"),
    linkPath: "/pricing",
  },
]

/* ─── Doctors (section 10) — CMS is source of truth ─── */

/* ─── Testimonials (section 11) — CMS is source of truth ─── */

/* ─── Google "G" icon ─── */

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 001 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  )
}

/* ─── Google rating with hover effect ─── */

function GoogleRating() {
  const t = useTranslations("home")
  const lp = useLocalePath()
  const [hovered, setHovered] = useState(false)
  return (
    <Link
      href={lp("/reviews")}
      className="flex items-center gap-2 self-center sm:ml-1 sm:self-auto"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="flex size-[24px] flex-shrink-0 items-center justify-center rounded-full bg-white/75 backdrop-blur-sm">
        <GoogleIcon />
      </div>
      <div className="flex items-center gap-px">
        {Array.from({ length: 5 }, (_, i) => {
          // Enter: left-to-right (star 0 first). Exit: right-to-left (star 4 first).
          const duration = hovered ? 90 : 65
          return (
            <div
              key={i}
              className="flex size-[16px] items-center justify-center"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 13.3333 12.68"
                aria-hidden="true"
              >
                <path
                  d="M6.66667 0L8.72667 4.17333L13.3333 4.84667L10 8.09333L10.7867 12.68L6.66667 10.5133L2.54667 12.68L3.33333 8.09333L0 4.84667L4.60667 4.17333L6.66667 0Z"
                  fill="#FBBF24"
                  style={{
                    opacity: hovered ? 1 : 0.8,
                    transition: `opacity ${duration}ms cubic-bezier(0.16, 1, 0.3, 1)`,
                  }}
                />
              </svg>
            </div>
          )
        })}
      </div>
      <span
        className="type-body-md leading-none transition-colors duration-300"
        style={{
          color: hovered ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0)",
          transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
          transform: "translateY(1px)",
        }}
      >
        <span className="font-medium">5.0</span>
        <span className="opacity-60">{t(k.homepageGoogleReviews)}</span>
      </span>
    </Link>
  )
}

/* ═══════════════════════════════════════════════════════
   PAGE COMPONENT
   ═══════════════════════════════════════════════════════ */

export default function HomePage({
  initialExperts = [],
  initialHeroVariant,
  showNewTestsPages = false,
  initialFaqs,
  initialTestimonials = [],
  servicesConfig = [],
  heroBackground,
}: {
  initialExperts?: ReviewerExpert[]
  /** Server-evaluated variant for flicker-free hero rendering. */
  initialHeroVariant?: string
  showNewTestsPages?: boolean
  /** CMS `services` control plane (per-country status + order + booking id),
   *  joined by slug onto the carousel. Empty = no overlay (fail-open to the
   *  hardcoded order + coming-soon rule). */
  servicesConfig?: CmsServiceConfig[]
  initialFaqs: {
    question: string
    answer: string
    slug?: string
    updatedAt?: string
    pages?: string[]
  }[]
  /** Server-fetched testimonials from the CMS. Empty array when the CMS
   *  is unreachable or has nothing for this locale — TestimonialsSection
   *  falls back to its hardcoded carousel in that case. */
  initialTestimonials?: Testimonial[]
  /** Hero `<picture>` rendered by the server component so the
   *  `ReactDOM.preload()` link tags land in the streamed `<head>` before
   *  any client JS executes — moves the LCP image fetch from
   *  post-hydration to first-byte. See `page.tsx` for the implementation. */
  heroBackground?: React.ReactNode
}) {
  const showNewTestsPagesEnabled = useNewTestsPagesEnabled(showNewTestsPages)
  const t = useTranslations("home")
  const ut = useTranslations("ui")
  const lp = useLocalePath()
  const locale = useLocale()
  const { code: country } = useCountry()
  const prices = useLivePrices(country)
  const { openWaitlist } = useWaitlist()
  const waitlistOnly = isWaitlistOnly(country)
  const heroVariant = useExperimentVariant(
    EXPERIMENTS["homepage-hero-v1"].flag,
    initialHeroVariant
  )
  const isHeroTest = heroVariant === "test"
  // Membership-first positioning — CH only since AT left the gate on
  // 2026-08-28. Country-derived (the country is in the URL), so server and
  // client agree with no flag read and no flicker; it takes precedence over
  // homepage-hero-v1 in the hero. NL/DE/AT keep the testing-first copy.
  // See lib/country-gates.ts.
  const isMembershipFirst = isAllowedInCountry(
    "feature:membership-first-positioning",
    country
  )
  // DE: Germany sells the blood check-up and its blood add-ons, nothing else,
  // so the homepage carries no MRI. The MRI package card and the "MRT-
  // Untersuchung" source pill are dropped outright rather than shown with a
  // "Bald verfügbar" pill: on the page that introduces the offer, a product
  // the visitor cannot buy reads as part of it. The MRI copy that stays in
  // `messages/de-DE.json` is unrendered, so this comes back by deleting the
  // flag once DE_EXPANSION opens MRI booking in Germany. The services carousel
  // and the testimonials are CMS-driven and gated there, not here.
  // country-gate-ignore: DE checkup-phase page shape, not a page gate
  const bloodOnly = country === "de"
  // AT: the mirror image. Austria sells the three MRI packages and nothing
  // else — the blood work inside Advanced and Pro is part of a package, not a
  // product with a price and a page of its own — so the blood card is dropped
  // from the selector instead of sending visitors to a `#blood-section` that
  // no longer exists on the Austrian /pricing. Delete once Austria sells a
  // standalone blood check-up.
  // country-gate-ignore: AT MRI-only page shape, not a page gate
  const mriOnly = country === "at"
  // DE: the Laura Philipp band runs on the homepage, straight after the
  // prevention stats. Its own flag rather than `bloodOnly` — which market
  // sells a scan and which market leads with an athlete are different
  // questions, and they will not be reversed together.
  // country-gate-ignore: DE homepage section choice, not a page gate
  const lauraBand = country === "de"
  // country-gate-ignore: DE homepage hero asset choice, not a page gate
  const videoHero = country === "de"
  // Bottom CTA banner's primary label. Membership-first markets (CH) lead
  // with "Book check-up" — the concluded winner of the main-CTA copy experiment
  // (see lib/experiments.ts), which replaced "Become a member". NL/DE/AT keep
  // the testing-first `mfCtaPrimary`. The hero is untouched because in CH it is
  // price-led (`mfHeroCtaPrimary`), not "Become a member".
  const mainCtaLabel = isMembershipFirst
    ? ut("mainCtaStartCheckup")
    : t(k.mfCtaPrimary)
  const translatedServices = useTranslatedServices({
    showNewTestsPages: showNewTestsPagesEnabled,
  })
  // Carousel ordering + status + per-country gating — driven SOLELY by the CMS
  // `services` control plane. `applyServicesConfig` filters `hidden`, tags
  // `coming_soon` / `waitlist` cards, and sorts by the per-country (or global)
  // order. When the config is empty (CMS unreachable / not configured) it
  // returns the content cards UNGATED in their default order; the loader
  // (`getServicesConfig`) fires Sentry in that case (fail-loud, not fail-open).
  const orderedServices = useMemo(
    () => applyServicesConfig(translatedServices, servicesConfig, country),
    [translatedServices, servicesConfig, country]
  )
  const countryLogos = useMemo(
    () =>
      LOGO_ITEMS.filter((l) => !l.countries || l.countries.includes(country)),
    [country]
  )
  const logoItemsDesktop = useMemo(
    () => countryLogos.map((l) => ({ ...l, label: t(l.tagKey) })),
    [countryLogos, t]
  )
  const logoItemsMobile = useMemo(
    () => countryLogos.map(({ src, alt, h, href }) => ({ src, alt, h, href })),
    [countryLogos]
  )

  return (
    <>
      <JsonLd data={createOrganizationSchema()} />
      <JsonLd data={createWebSiteSchema(country, locale)} />
      {/* ─── Hero (full-screen) ─── */}
      {/* `min-h` rather than a fixed `h` (AHD-10495): the content is
          bottom-anchored on mobile, so on a short viewport (360×610 — a
          Galaxy S25 with the browser chrome showing) a tall localisation
          grew UPWARDS out of the hero and the French headline ran under the
          fixed navbar. With a min-height the section grows to fit its
          content instead, and the `pt` below reserves the navbar's band. */}
      <section className="relative -mb-px flex min-h-[max(600px,calc(100svh-36px))] overflow-hidden bg-neutral-950">
        {/* Hero background `<picture>` is rendered by the server component
            (page.tsx) so its `ReactDOM.preload()` link tags land in the
            streamed `<head>` before any client JS executes. */}
        {heroBackground}

        {/* DE runs the membership hero reel over its poster (page.tsx picks
            the matching poster). Sources attach after idle, not on Save-Data. */}
        {videoHero && (
          <HeroBackgroundVideo
            mobile={{
              src: "/videos/membership-hero-mobile.mp4",
              type: "video/mp4",
            }}
            desktop={[
              {
                src: "/videos/membership-hero.av1.mp4",
                type: 'video/mp4; codecs="av01.0.05M.08"',
              },
              { src: "/videos/membership-hero.mp4", type: "video/mp4" },
            ]}
          />
        )}

        {/* Mobile overlay: neutral-950 at 50% for text readability */}
        <div className="absolute inset-0 z-[1] bg-neutral-950/50 md:hidden" />

        {/* Gradient overlays — Safari renders `mix-blend-mode: color-burn`
            inconsistently (muddy grays instead of crushed blacks), so we
            use plain alpha gradients instead. */}
        {/* Nav overlay: top gradient */}
        <div
          className="absolute top-0 right-0 left-0 z-[1]"
          style={{
            height: 124,
            background:
              "linear-gradient(rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)",
          }}
        />
        {/* Hero overlay: left-side gradient for text readability */}
        <div
          className="absolute top-0 bottom-0 left-0 z-[1] hidden md:block"
          style={{
            width: "60%",
            background:
              "linear-gradient(90deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.15) 60%, transparent 100%)",
          }}
        />
        {/* Logos overlay: bottom gradient */}
        <div
          className="absolute right-0 bottom-0 left-0 z-[1]"
          style={{
            height: 120,
            background:
              "linear-gradient(rgba(0,0,0,0) 0%, rgba(0,0,0,0.5) 100%)",
          }}
        />

        {/* Content — bottom-aligned on mobile, centered on desktop; padded above marquee.
            Membership markets: on lg the content column stretches to the full
            hero height (lg:items-stretch + lg:h-full below) so the headline group
            can center while the USP chips drop to the hero base — the space the
            logo marquee vacated. Testing-first markets keep lg:items-center. */}
        <div
          className={`relative z-[2] flex w-full items-end pt-20 pb-16 md:pb-[100px] lg:pt-16 ${
            isMembershipFirst
              ? "lg:items-stretch lg:pb-12"
              : "lg:items-center lg:pb-20"
          }`}
        >
          <div className="mx-auto w-full max-w-[1440px] px-4 md:px-10">
            <div
              className={`flex flex-col ${isMembershipFirst ? "lg:h-full" : ""}`}
            >
              {/* Headline group. `contents` keeps the testing-first markets +
                  mobile byte-identical (the wrapper is layout-transparent); only lg
                  in membership markets promotes it to a flex column that centers in
                  the freed space, pushing the chips below it to the hero base. */}
              <div
                className={
                  isMembershipFirst
                    ? "contents lg:flex lg:flex-1 lg:flex-col lg:justify-center"
                    : "contents"
                }
              >
                {/* Google rating — above title on tablet only.
                  Hidden on phones (< md) to reduce hero density on small
                  viewports per AHD-6127; on lg+ desktop the inline rating
                  next to the CTAs takes over. */}
                <div className="mb-3 hidden md:block lg:hidden">
                  <GoogleRating />
                </div>
                {/* Ahead membership announcement — links to /membership.
                  Gate config in lib/country-gates.ts (section:home.hero-membership-pill).
                  Hidden in membership markets — the whole hero is already about
                  membership, and the annual price rides in the primary CTA. */}
                {!isMembershipFirst && (
                  <CountryGate id="section:home.hero-membership-pill">
                    <div className="pill-blur-in mb-3 md:mb-6">
                      <Link href={lp("/membership")}>
                        <NewMembershipPill
                          label={t(k.homepageMembershipPill)}
                        />
                      </Link>
                    </div>
                  </CountryGate>
                )}
                {/* No price pill in membership markets: the annual membership
                  price lives inside the primary CTA instead, so it sits on the
                  action rather than above it. */}
                {!isMembershipFirst &&
                  isHeroTest &&
                  t(k.heroPricingPill, {
                    price: prices.fullPrices.blood ?? "",
                  }) && (
                    <div className="mb-3 hidden lg:block">
                      <span className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-4 py-1.5 text-sm font-medium text-white backdrop-blur-md">
                        {t(k.heroPricingPill, {
                          price: prices.fullPrices.blood ?? "",
                        })}
                      </span>
                    </div>
                  )}
                {/* Plain <h1> — no framer-motion wrapper on the LCP candidate.
                  An earlier pass dropped `opacity: 0` (f595a42); this one
                  drops the remaining `transform: translateY(24px)` slide-up.
                  Even with the text visible at first paint, the inline
                  transform from `initial` was being applied in SSR and the
                  element's paint position was tied to framer-motion
                  hydration. Removing the wrapper renders the headline at its
                  final position with zero JS dependency. */}
                {/* Headline + subhead swap. */}
                <h1 className="type-display-lg max-w-full text-white md:max-w-[610px]">
                  {isMembershipFirst
                    ? // Force the two-line break at the comma
                      // ("Your health," / "tracked over time.") regardless of width.
                      (() => {
                        const [lead, rest] = t(k.mfHeroHeading).split(/,\s(.+)/)
                        return rest ? (
                          <>
                            {lead},
                            <br />
                            {rest}
                          </>
                        ) : (
                          t(k.mfHeroHeading)
                        )
                      })()
                    : t(k.heroHeading)}
                </h1>

                <p
                  className="type-body-xl mt-3 font-normal text-white md:mt-4"
                  style={{ maxWidth: 610 }}
                >
                  {isMembershipFirst ? t(k.mfHeroSubtitle) : t(k.heroSubtitle)}
                </p>

                <div className="mt-5 md:mt-6">
                  {/* Buttons + Google rating — stacked on mobile, inline row on sm+ */}
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    {/* Membership markets lead with "Become a member" (primary)
                      + "How it works" (secondary → /membership, the explainer
                      page). */}
                    {isMembershipFirst ? (
                      <>
                        <BookingCta
                          forMembership
                          category="membership"
                          overrideHref={membershipBookingAppUrl(locale)}
                          trackingLabel="Become a member"
                          trackingLocation="hero"
                          className="group text-aubergine-900 type-title-md relative inline-flex h-[44px] w-full items-center justify-center overflow-hidden rounded-full bg-white px-10 font-medium whitespace-nowrap transition-shadow duration-200 hover:shadow-[inset_0_0_0_999px_rgba(0,0,0,0.03)] sm:w-auto"
                        >
                          <span className="inline-block transition-transform duration-200 ease-out group-hover:-translate-x-2">
                            {t(k.mfHeroCtaPrimary)}
                          </span>
                          <span className="absolute right-4 translate-x-3 opacity-0 transition-[transform,opacity] duration-200 ease-out group-hover:translate-x-0 group-hover:opacity-100">
                            <ArrowRightIcon />
                          </span>
                        </BookingCta>
                        <a
                          href={lp("/membership")}
                          onClick={() => track.ctaClick("How it works", "hero")}
                        >
                          <Button
                            variant="outline"
                            className="w-full border-white/30 text-white hover:border-white/30 hover:bg-white/10 sm:w-auto"
                          >
                            {t(k.heroCtaSecondary)}
                          </Button>
                        </a>
                      </>
                    ) : (
                      <>
                        {/* Bare booking URL (no packageType) so the hero matches
                            the navbar + bottom-CTA destination — the three
                            homepage CTAs must land identically or the
                            membership-first control arm measures a URL split,
                            not the CTA (AHD-8605). overrideHref keeps the
                            waitlist/coming-soon gating in BookingCta intact. */}
                        <BookingCta
                          overrideHref={bookingUrlForCountry(country, locale)}
                          trackingLabel="Book check-up"
                          trackingLocation="hero"
                          className="group text-aubergine-900 type-title-md relative inline-flex h-[44px] w-full items-center justify-center overflow-hidden rounded-full bg-white px-10 font-medium whitespace-nowrap transition-shadow duration-200 hover:shadow-[inset_0_0_0_999px_rgba(0,0,0,0.03)] sm:w-auto"
                        >
                          <span className="inline-block transition-transform duration-200 ease-out group-hover:-translate-x-2">
                            {/* Waitlist market: the CTA action is the waitlist
                                modal, so the label says so (2026-07-29). */}
                            {waitlistOnly
                              ? ut("joinWaitlist")
                              : t(k.heroCtaPrimary)}
                          </span>
                          <span className="absolute right-4 translate-x-3 opacity-0 transition-[transform,opacity] duration-200 ease-out group-hover:translate-x-0 group-hover:opacity-100">
                            <ArrowRightIcon />
                          </span>
                        </BookingCta>
                        {isHeroTest ? (
                          <>
                            {/* Mobile: always control CTA (test variant hidden below lg) */}
                            <a
                              href={lp("/how-it-works")}
                              onClick={() =>
                                track.ctaClick("How it works", "hero")
                              }
                              className="lg:hidden"
                            >
                              <Button
                                variant="outline"
                                className="w-full border-white/30 text-white hover:border-white/30 hover:bg-white/10 sm:w-auto"
                              >
                                {t(k.heroCtaSecondary)}
                              </Button>
                            </a>
                            {/* Desktop: test CTA */}
                            <a
                              href={externalQuizUrl(locale, {
                                utm: {
                                  utm_source: "website",
                                  utm_medium: "hero_cta",
                                  utm_campaign: "homepage_hero_v1",
                                  utm_content: "find_your_test",
                                },
                              })}
                              onClick={() =>
                                track.ctaClick("Find your test", "hero")
                              }
                              className="hidden lg:inline-block"
                            >
                              <Button
                                variant="outline"
                                className="w-full border-white/30 text-white hover:border-white/30 hover:bg-white/10 sm:w-auto"
                              >
                                {t(k.heroCtaFindTest)}
                              </Button>
                            </a>
                          </>
                        ) : (
                          <a
                            href={lp("/how-it-works")}
                            onClick={() =>
                              track.ctaClick("How it works", "hero")
                            }
                          >
                            <Button
                              variant="outline"
                              className="w-full border-white/30 text-white hover:border-white/30 hover:bg-white/10 sm:w-auto"
                            >
                              {t(k.heroCtaSecondary)}
                            </Button>
                          </a>
                        )}
                      </>
                    )}
                    {/* Google rating — inline on desktop only */}
                    <div className="hidden lg:block">
                      <GoogleRating />
                    </div>
                  </div>
                </div>
              </div>
              {/* Above-fold USP chips (Function-style): bold stat + subtext,
                  divided, each links to its proof page. On lg the flex-1 headline
                  wrapper above pushes these to the hero base. */}
              {isMembershipFirst && (
                <div className="mt-7 flex flex-wrap gap-x-5 gap-y-3 max-sm:grid max-sm:grid-cols-3 max-sm:gap-0 max-sm:divide-x max-sm:divide-white/15 md:mt-8 md:gap-x-6">
                  {MF_USP_CHIPS.map((chip) => (
                    <Link
                      key={chip.path}
                      href={lp(chip.path)}
                      onClick={() =>
                        track.ctaClick(t(chip.statKey), "hero-usp-chip")
                      }
                      className="group border-white/25 max-sm:px-2.5 max-sm:text-center sm:[&:not(:first-child)]:border-l sm:[&:not(:first-child)]:pl-5 md:sm:[&:not(:first-child)]:pl-6"
                    >
                      <span className="type-title-md max-sm:type-title-sm block font-medium text-white decoration-white/40 underline-offset-4 group-hover:underline">
                        {t(chip.statKey)}
                      </span>
                      <span className="type-body-sm block text-white/70">
                        {t(chip.subKey)}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Logo marquee — overlaid at bottom of hero (desktop only). Dropped in
            membership markets — the marquee moves to a light band BELOW the hero
            (see next block) so the above-fold USP chips own the hero. */}
        {!isMembershipFirst && (
          <div className="absolute right-0 bottom-0 left-0 z-10 hidden pb-5 lg:block">
            <LogoMarquee
              items={logoItemsDesktop}
              theme="dark"
              gapClassName="gap-20"
            />
          </div>
        )}
      </section>

      {/* Logo marquee below hero. Testing-first markets: mobile + tablet only
          (desktop uses the overlay above). Membership markets: every breakpoint,
          as a light band (à la the membership page) — there's no overlay. */}
      <div className={isMembershipFirst ? "py-8 md:py-10" : "py-6 lg:hidden"}>
        <LogoMarquee
          items={isMembershipFirst ? logoItemsDesktop : logoItemsMobile}
          theme="light"
          duration={80}
          gapClassName={isMembershipFirst ? "gap-12 md:gap-20" : "gap-12"}
        />
      </div>

      {/* ─── Value proposition cards ─── */}
      {/* Membership markets swap to action-loop value props. */}
      <ValuePropCards
        labels={(isMembershipFirst ? MF_VALUE_PROP_KEYS : VALUE_PROP_KEYS).map(
          (key) => t(key)
        )}
      />

      {/* ─── Ahead membership (promoted) ─── */}
      {/* Membership markets carry a dedicated membership section directly under
          the value-prop cards, above "Why early prevention matters". This
          shipped as the winner of homepage-membership-section-v1 — the block was
          dropped unmeasured in PR #2104 and won on its own when re-run. CH only:
          the `isMembershipFirst` branch AND the ch-only
          `section:home.membership-banner` gate (AT is membership-first but
          outside the banner gate). Testing-first markets render the same block
          mid-page on the `!isMembershipFirst` branch further down. */}
      {isMembershipFirst && (
        <CountryGate id="section:home.membership-banner">
          <HomepageMembershipSection />
        </CountryGate>
      )}

      {/* ─── Stats: "Why early prevention matters" ─── */}
      <section className="lazy-section py-16 md:py-24">
        <div className="px-4 md:px-10">
          <h2 className="type-display-md text-center text-neutral-900">
            {t(k.statsHeading)}
          </h2>
          <p className="type-body-lg mx-auto mt-4 max-w-2xl text-center text-neutral-600">
            {t(k.statsDescription)}
          </p>
        </div>

        <div className="mx-auto mt-12 grid max-w-[1440px] grid-cols-1 items-stretch gap-2 px-4 md:grid-cols-3 md:px-10">
          {STATS_META.map((stat) => (
            <div key={stat.valueKey} className="h-full">
              <StatCard
                className="h-full"
                value={t(stat.valueKey)}
                description={t(stat.descKey)}
                highlight={t(stat.highlightKey)}
                highlightColor={stat.highlightColor}
                source={t(stat.sourceKey)}
                sourceUrl={"sourceUrl" in stat ? stat.sourceUrl : undefined}
              />
            </div>
          ))}
        </div>
      </section>

      {/* ─── Laura Philipp CTA band ─── */}
      {/* DE only, straight after the prevention stats: the athlete band turns
          the "why prevention matters" argument into a named person's routine
          before the page moves on to other people's reviews.
          `showImaging={false}` is not a guess: Germany sells no scan, so the
          eyebrow and the booking CTA take their blood-only shape.
          `copy="homepage"` swaps the campaign page's closing argument for the
          band's own introduction to Laura, because here she is being met for
          the first time rather than argued from.
          The tracking location is overridden so these clicks stay separable
          from the campaign page's own bottom CTA in the funnel. */}
      {lauraBand && (
        <AthleteClosingCta
          showImaging={false}
          copy="homepage"
          trackingLabel="homepage_laura_cta"
          trackingLocation="homepage-laura-band"
        />
      )}

      {/* ─── Real results from real people ─── */}
      <TestimonialsSection cmsTestimonials={initialTestimonials} />

      {/* ─── Services carousel: "It's time to get the full picture" ─── */}
      {/* Austria has one card. `DEFAULT_SERVICES` is filtered by
          `isServicePathHidden`, and AT marks every standalone test
          `at-de: false`, so 1 of 17 survives (CH 17, NL 16, DE 5). A single
          fixed-width card in an edge-to-edge scroller parks at the left and
          leaves the rest of the row empty, and the section's own promise —
          imaging AND blood tests — is one Austria cannot make. The section
          earns its place by showing range; Austria has none to show yet, and
          the full-body MRI already leads the page and the pricing tiers. It
          comes back on its own as the catalogue grows. */}
      {!mriOnly && (
        <section className="lazy-section py-16 md:py-24">
          <div>
            <div className="mx-auto mb-8 flex max-w-[1440px] flex-col gap-4 px-4 md:flex-row md:items-end md:justify-between md:px-10">
              {/* Membership markets reframe the carousel as what membership covers. */}
              <h2 className="type-display-sm text-neutral-900">
                {isMembershipFirst
                  ? t(k.mfServicesHeading)
                  : t(k.servicesHeading)}
              </h2>
              <p className="type-body-lg max-w-[340px] text-neutral-600 md:text-right lg:max-w-[460px]">
                {isMembershipFirst
                  ? t(k.mfServicesDescription)
                  : t(k.servicesDescription)}
              </p>
            </div>
          </div>

          <ServicesCarousel
            items={orderedServices}
            learnMoreLabel={ut("learnMore")}
            disclaimerLabel={ut("illustrativeSamples")}
          />
        </section>
      )}

      {/* ─── How it works — scroll-driven horizontal cards ─── */}
      {/* In membership markets the heading + step copy become the membership
          lifecycle (the decorative panels are shared). */}
      <HowItWorksScroll
        heading={
          isMembershipFirst ? t(k.mfHowItWorksHeading) : t(k.howItWorksHeading)
        }
        subtitle={
          isMembershipFirst
            ? t(k.mfHowItWorksDescription)
            : t(k.howItWorksDescription)
        }
        steps={[
          {
            number: "1",
            color: "rgb(80, 131, 208)",
            title: isMembershipFirst ? t(k.mfStep1Title) : t(k.step1Title),
            description: isMembershipFirst
              ? t(k.mfStep1Description)
              : t(k.step1Description),
            panel: (
              <Step1Panel
                hsCrpText={t(k.panelHsCrpText)}
                actionsText={t(k.panelTwoActions)}
              />
            ),
          },
          {
            number: "2",
            color: "rgb(142, 155, 21)",
            title: isMembershipFirst ? t(k.mfStep2Title) : t(k.step2Title),
            description: isMembershipFirst
              ? t(k.mfStep2Description)
              : t(k.step2Description),
            panel: (
              <Step2Panel
                label={t(k.panelHba1cLabel)}
                value="5.9"
                unit="%"
                statusLabel={t(k.panelIntermediate)}
                diabetesRange={t(k.panelDiabetesRange)}
                preDiabetesRange={t(k.panelPreDiabetesRange)}
                normalRange={t(k.panelNormalRange)}
              />
            ),
          },
          {
            number: "3",
            color: "rgb(100, 80, 140)",
            title: isMembershipFirst ? t(k.mfStep3Title) : t(k.step3Title),
            description: isMembershipFirst
              ? t(k.mfStep3Description)
              : t(k.step3Description),
            panel: (
              <Step3Panel
                value="4.8"
                unitLabel={t(k.panelMmolUnit)}
                label={t(k.panelFastingGlucose)}
              />
            ),
          },
          {
            number: "4",
            color: "rgb(72, 33, 74)",
            title: isMembershipFirst ? t(k.mfStep4Title) : t(k.step4Title),
            description: isMembershipFirst
              ? t(k.mfStep4Description)
              : t(k.step4Description),
            panel: (
              <Step4Panel
                dateText={t(k.panelAppointmentDate)}
                timeText={t(k.panelAppointmentTime)}
                confirmedText={t(k.panelConfirmed)}
                locationCity={t(k.panelLocationCity)}
                locationAddress={t(k.panelLocationAddress)}
              />
            ),
          },
        ]}
      />

      {/* ─── From scattered medical data to useful insights ─── */}
      <section className="lazy-section py-12 md:py-16">
        <ScatteredData
          variant="light"
          heading={t(k.scatteredHeading)}
          headingHighlight=""
          subtitle={t(k.scatteredSubtitle)}
          pillLabels={{
            medicalRecord: t(k.pillMedicalRecord),
            mriScan: bloodOnly ? undefined : t(k.pillMriScan),
            prescriptions: t(k.pillPrescriptions),
            bloodWork: t(k.pillBloodWork),
            actionPlan: t(k.pillActionPlan),
            lifestyleGoals: t(k.pillLifestyleGoals),
          }}
          cardName={t(k.cardName)}
          cardBiomarkers={t(k.cardBiomarkers)}
          cardActionPoints={t(k.cardActionPoints)}
        />
        <div className="mt-10 text-center">
          <Link href={lp("/report")}>
            <Button variant="outline">{ut("exploreReport")}</Button>
          </Link>
        </div>
      </section>

      {/* ─── Ahead membership ─── */}
      {/* Hidden on DE (and AT): membership isn't offered in the coming-soon
          EUR markets. Gate config in lib/country-gates.ts.
          Testing-first markets render the block here mid-page; membership
          markets render it in the promoted position above, under the value
          props (or nothing at all outside the ch-only banner gate). */}
      {!isMembershipFirst && (
        <CountryGate id="section:home.membership-banner">
          <HomepageMembershipSection />
        </CountryGate>
      )}

      {/* ─── Health changes, prevention should too ─── */}
      <section className="lazy-section py-16 md:py-24">
        <div>
          <div className="mx-auto mb-10 flex max-w-[1440px] flex-col gap-4 px-4 md:flex-row md:items-end md:justify-between md:px-10">
            <h2 className="type-display-sm max-w-md text-neutral-900">
              {t(k.ageHeading)}
            </h2>
            <p className="type-body-lg max-w-md text-neutral-600">
              {t(k.ageDescription)}
            </p>
          </div>
        </div>

        <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-2 px-4 md:grid-cols-3 md:px-10">
          {AGE_GROUPS_META.map((group) => (
            <div key={group.rangeKey} className="h-full">
              <AgeCard
                ageRange={t(group.rangeKey)}
                title={t(group.titleKey)}
                description={t(group.descKey)}
                imageSrc={group.image}
                linkText={t(group.linkKey)}
                linkHref={lp(group.linkPath)}
                className="h-full"
              />
            </div>
          ))}
        </div>
      </section>

      {/* ─── The experts behind your health journey ─── */}
      {/* Hidden on DE for now (AHD-6680).
          country-gate-ignore: DE checkup-phase content hide, not a page gate */}
      {country !== "de" && (
        <DoctorsSection
          initialExperts={initialExperts}
          columnHeading={
            <h2 className="type-display-sm max-w-md text-neutral-900">
              {t(k.expertsHeading)}
            </h2>
          }
        />
      )}

      {/* ─── Find the right starting point ─── */}
      {/* Membership markets lead the selector with membership. */}
      <PackageSelector
        heading={
          isMembershipFirst ? t(k.mfPricingHeading) : t(k.pricingHeading)
        }
        description={
          isMembershipFirst
            ? t(k.mfPricingDescription)
            : t(k.pricingDescription)
        }
        blood={
          mriOnly
            ? undefined
            : {
                image: {
                  src: blobMediaUrl(
                    "/images/homepage/service-blood-cells.jpeg"
                  ),
                  alt: "Blood test",
                },
                eyebrow: t(k.pricingBloodEyebrow),
                title: t(k.pricingBloodTitle),
                description: t(k.pricingBloodDescription),
                forLabel: t(k.pricingBloodForLabel),
                items: [
                  t(k.pricingBloodItem1),
                  t(k.pricingBloodItem2Homepage),
                  t(k.pricingBloodItem3),
                ],
                priceFrom: t(k.pricingBloodFrom),
                price: t(k.pricingBloodPrice, {
                  price: prices.fullPrices.blood ?? "",
                }),
                ctaLabel: t(k.pricingBloodLink),
                href: `${lp("/pricing")}#blood-section`,
              }
        }
        mri={
          bloodOnly
            ? undefined
            : {
                image: {
                  src: blobMediaUrl(
                    "/images/homepage/service-mri-machine.jpeg"
                  ),
                  alt: "MRI scanner",
                },
                eyebrow: t(k.pricingMriEyebrow),
                title: t(k.pricingMriTitle),
                description: t(k.pricingMriDescription),
                forLabel: t(k.pricingMriForLabel),
                items: [
                  t(k.pricingMriItem1),
                  t(k.pricingMriItem2),
                  t(k.pricingMriItem3),
                ],
                priceFrom: t(k.pricingMriFrom),
                price: t(k.pricingMriPrice, {
                  price: prices.fullPrices.core ?? "",
                }),
                ctaLabel: t(k.pricingMriLink),
                href: `${lp("/pricing")}#mri-section`,
                // Markets where MRI packages aren't bookable yet get a
                // coming-soon pill instead of a booking CTA.
                comingSoonLabel: isComingSoon(country, "mri")
                  ? ut("comingSoon")
                  : undefined,
              }
        }
      />

      {/* ─── Insurance ─── */}
      <InsuranceSection trackingLocation="homepage-insurance" />

      {/* ─── FAQ ─── */}
      {/* DE: keep the inline FAQ accordion but drop the "see all FAQs" link —
          the dedicated /faq page is intentionally unreachable from DE nav. */}
      <FaqSection
        heading={t(k.faqHeading)}
        linkLabel={country === "de" ? undefined : t(k.faqLink)}
        linkHref={country === "de" ? undefined : lp("/faq")}
        items={initialFaqs}
        currentPage="homepage"
      />

      {/* ─── Bottom CTA ─── */}
      <div>
        {/* Membership markets: title/subtitle + primary CTA → become a member. */}
        <CtaBanner
          title={isMembershipFirst ? t(k.mfCtaTitle) : t(k.ctaTitle)}
          subtitle={isMembershipFirst ? t(k.mfCtaSubtitle) : t(k.ctaSubtitle)}
          imageSrc={blobMediaUrl("/images/homepage/cta-bottom.jpeg")}
          primaryCta={
            // Waitlist market: every primary CTA reads "Join waitlist"
            // (2026-07-29) — the action already opens the waitlist modal.
            waitlistOnly
              ? { label: ut("joinWaitlist"), onClick: () => openWaitlist() }
              : isMembershipFirst
                ? {
                    label: mainCtaLabel,
                    href: membershipBookingAppUrl(locale),
                  }
                : {
                    label: t(k.ctaPrimary),
                    href: bookingUrlForCountry(country, locale),
                  }
          }
          secondaryCta={{ label: t(k.ctaSecondary), href: lp("/contact") }}
        />
      </div>

      {/* ─── Footer ─── */}
    </>
  )
}
